import { FunctionComponent, useState, useEffect } from "react"
import { CartStructure, ShippingMethod } from "../../interfaces/SaleorTypes"
import { useCreateCheckoutMutation } from "../../../hooks-saleor/checkoutCreateMutation"
import { useStripeTransactionInitialize } from "../../../hooks-saleor/useStripeTransactionInitialize"
import { useCheckoutDeliveryMethodUpdateMutation } from "../../../hooks-saleor/checkoutDeliveryMethodUpdateMutation"
import { useCheckoutPaymentCreate } from "../../../hooks-saleor/useCheckoutPaymentCreate"
import { StripeAppWidget, StripePluginWidget } from "./CheckoutStripeWidget"
import { useCheckoutCompleteMutation } from "../../../hooks-saleor/checkoutCompleteMutation"
import ShippingFormWidget from "./CheckoutShippingFormWidget"
import ShippingMethodWidget from "./CheckoutShippingMethodWidget"
import CheckoutJourneyIntroSection from "./CheckoutJourneyIntroSection"
import { useUser } from "../../../hooks/useUser"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import { CheckoutType } from "../../../graphql/mutations/createUnpaidOrder"
import BodyMedium from "../../atoms/typography/BodyMedium"
import { theme } from "../../theme"
import CheckoutUnpaidOrder from "./CheckoutUnpaidOrder"
import { SELECTED_CHANNEL_SLUG } from "../../../utils/saleor-constants"
import { IS_USING_STRIPE_PLUGIN_GATEWAY } from "../../../utils/saleor-constants"

interface CheckoutJourneyProps {
  cart: CartStructure[]
  onPassShippingMethod: (ShippingMethod: ShippingMethod) => void
  onLockSummary: (flag: boolean) => void
  onDirectPayment: (flag: boolean) => void
  onSetModal: (flag: boolean, view: string) => void
  onClearCart: () => void
}

const CheckoutJourney: FunctionComponent<CheckoutJourneyProps> = ({
  cart,
  onPassShippingMethod,
  onLockSummary,
  onDirectPayment,
  onSetModal,
  onClearCart,
}) => {
  const [checkoutType, setCheckoutType] = useState<CheckoutType>()
  const [checkoutId, setCheckoutId] = useState()
  const [selectedShipping, setSelectedShipping] = useState<ShippingMethod>()
  const [clientSecret, setClientSecret] = useState()
  const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>()
  const [isBillingSame, setIsBillingSame] = useState(true)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState<HTMLFormControlsCollection>()
  const [transactionId, setTransactionId] = useState<string>()

  const { createCheckoutMutation, data } = useCreateCheckoutMutation()
  const { checkoutCompleteMutation } = useCheckoutCompleteMutation()
  const { checkoutPaymentCreate } = useCheckoutPaymentCreate()
  const { stripeTransactionInitialize } = useStripeTransactionInitialize()
  const { checkoutDeliveryMethodUpdateMutation } = useCheckoutDeliveryMethodUpdateMutation()

  const { user, loading: userLoading } = useUser()

  const createCheckoutSession = (e: any) => {
    e.preventDefault()
    setLoading(true)
    const formData = e.target.elements
    setFormData(formData)
    const lines = cart.map((product) => {
      return {
        quantity: product.amount,
        variantId: product.product.variants[0].id,
      }
    })

    createCheckoutMutation({
      variables: {
        channel: SELECTED_CHANNEL_SLUG,
        email: formData["billing-email"].value,
        lines: lines,
        bFirstName: formData["billing-first-name"].value,
        bLastName: formData["billing-last-name"].value,
        bStreetAdress1: formData["billing-street-address-1"].value,
        bStreetAdress2: formData["billing-street-address-2"]?.value,
        bPostalCode: formData["billing-postal-code"].value,
        bCity: formData["billing-city"].value,
        bCountry: formData["billing-country"].value,
        bCountryArea: formData["billing-country-area"] ? formData["billing-country-area"].value : undefined,
        bPhone: formData["billing-phone"].value,
        bCompany: formData["billing-company"].value,
        sFirstName: isBillingSame ? formData["billing-first-name"].value : formData["shipping-first-name"].value,
        sLastName: isBillingSame ? formData["billing-last-name"].value : formData["shipping-last-name"].value,
        sStreetAdress1: isBillingSame
          ? formData["billing-street-address-1"].value
          : formData["shipping-street-address-1"].value,
        sStreetAdress2: isBillingSame
          ? formData["billing-street-address-2"]?.value
          : formData["shipping-street-address-2"]?.value,
        sCity: isBillingSame ? formData["billing-city"].value : formData["shipping-city"].value,
        sPostalCode: isBillingSame ? formData["billing-postal-code"].value : formData["shipping-postal-code"].value,
        sCountry: isBillingSame ? formData["billing-country"].value : formData["shipping-country"].value,
        sCountryArea: formData["billing-country-area"]
          ? isBillingSame
            ? formData["billing-country-area"].value
            : formData["shipping-country-area"].value
          : undefined,
        sPhone: isBillingSame ? formData["billing-phone"].value : formData["shipping-phone"].value,
      },
    }).then((response) => {
      setCheckoutId(response.data.checkoutCreate.checkout.id)
      setShippingMethods(response.data.checkoutCreate.checkout.shippingMethods)
      onLockSummary(true)
      setLoading(false)
    })
  }

  useEffect(() => {
    if (checkoutId && selectedShipping) {
      setLoading(true)
      checkoutDeliveryMethodUpdateMutation({
        variables: { checkoutId: checkoutId, deliveryMethodId: selectedShipping.id },
      }).then((response) => {
        if (checkoutType === "rental" || checkoutType === "leasing") {
          setLoading(false)
          return
        }
        if (IS_USING_STRIPE_PLUGIN_GATEWAY) {
          // stripe plugin
          checkoutPaymentCreate({
            checkoutId: checkoutId,
            amount: response.data.checkoutDeliveryMethodUpdate.checkout.totalPrice.gross.amount,
          }).then((response) => {
            checkoutCompleteMutation({
              variables: {
                checkoutId: checkoutId,
                paymentData: JSON.stringify({
                  payment_method_types: ["card"],
                }),
              },
            }).then((response) => {
              setClientSecret(JSON.parse(response.data.checkoutComplete.confirmationData).client_secret)
              setLoading(false)
            })
          })
        } else {
          stripeTransactionInitialize({
            checkoutId: checkoutId,
            amount: response.data.checkoutDeliveryMethodUpdate.checkout.totalPrice.gross.amount,
            currency: response.data.checkoutDeliveryMethodUpdate.checkout.totalPrice.gross.currency.toLowerCase(),
            paymentMethodTypes: ["card"],
          }).then((response) => {
            setTransactionId(response.data?.transactionInitialize?.transaction?.id)
            setClientSecret(response.data?.transactionInitialize?.data?.paymentIntent?.client_secret)
            setLoading(false)
          })
        }
      })
    }
  }, [selectedShipping])

  const resetJourney = () => {
    setCheckoutType(undefined)
    setClientSecret(undefined)
    setSelectedShipping(undefined)
    setShippingMethods(undefined)
    onLockSummary(false)
    onDirectPayment(true)
  }

  const onRadioClick = (e: any) => {
    setIsBillingSame(e.target.checked)
  }

  const onShippingMethodClick = (method: ShippingMethod) => {
    setSelectedShipping(method)
    onPassShippingMethod(method)
  }

  const onCheckoutTypeClick = (type: CheckoutType) => {
    resetJourney()
    setCheckoutType(type)
  }

  return (
    <div>
      {checkoutType ? (
        <div>
          {userLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <ShippingFormWidget
                setOpenModal={onSetModal}
                user={user}
                onDirectPayment={onDirectPayment}
                isShippingFound={Boolean(shippingMethods)}
                onCheckoutTypeClick={onCheckoutTypeClick}
                checkoutType={checkoutType}
                isBillingSame={isBillingSame}
                onRadioClick={onRadioClick}
                createCheckoutSession={createCheckoutSession}
              />

              {data?.checkoutCreate?.errors?.length > 0 && (
                <div>
                  {data?.checkoutCreate?.errors?.map((error: { field: string; code: string }) => (
                    <BodyMedium color={theme.colors.red} key={error.field}>
                      {error.field} {error.code}
                    </BodyMedium>
                  ))}
                </div>
              )}
            </>
          )}
          {shippingMethods && (
            <>
              {shippingMethods.length < 1 ? (
                <BodyMedium color={theme.colors.red}>
                  No shipping available. Please contact sales@satcube.com
                </BodyMedium>
              ) : (
                <ShippingMethodWidget onShippingMethodClick={onShippingMethodClick} shippingMethods={shippingMethods} />
              )}
            </>
          )}
          {selectedShipping && clientSecret && checkoutId && (IS_USING_STRIPE_PLUGIN_GATEWAY || transactionId) ? (
            IS_USING_STRIPE_PLUGIN_GATEWAY ? (
              <StripePluginWidget
                onClearCart={onClearCart}
                cart={cart}
                clientSecret={clientSecret}
                checkoutId={checkoutId}
              />
            ) : (
              <StripeAppWidget
                onClearCart={onClearCart}
                cart={cart}
                clientSecret={clientSecret}
                checkoutId={checkoutId}
                transactionId={transactionId!}
              />
            )
          ) : (
            selectedShipping && loading && <LoadingSpinner />
          )}
          {selectedShipping &&
            checkoutId &&
            formData &&
            (checkoutType === "rental" || checkoutType === "leasing") &&
            !loading && (
              <CheckoutUnpaidOrder
                cart={cart}
                checkoutId={checkoutId}
                formData={formData}
                onClearCart={onClearCart}
                checkoutType={checkoutType}
              />
            )}
        </div>
      ) : (
        <CheckoutJourneyIntroSection onCheckoutTypeClick={onCheckoutTypeClick} cart={cart} />
      )}
    </div>
  )
}

export default CheckoutJourney
